/*
 * TCT Lead Finder — Admin tab
 * Adapted from the sign-shop template's AdminLeads page but compressed to
 * fit the existing TCT Admin tab shell. Uses `apiClient` + `X-Admin-Password`
 * (no separate auth flow) and the shop's brand tokens.
 *
 * Three tabs:
 *   1. Search — Google Places lookup, save winners
 *   2. Saved  — filter/manage/CSV-export the pipeline
 *   3. Follow-ups — overdue/today/this-week dashboard
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Search, Save, Loader2, ExternalLink, MapPin, Phone, Globe, Star,
  Filter, Download, Trash2, CheckCircle2, Mail, Wand2, Copy,
  Sparkles, Calendar, Users, ShieldAlert, ShieldCheck, Bookmark, X, Plus,
  Send, Eye, Inbox, RefreshCw,
} from "lucide-react";
import { apiClient } from "../../lib/api";

const STATUS_STYLES = {
  new:              "bg-purple-500/15 text-purple-300 border-purple-400/40",
  hot_lead:         "bg-red-500/15 text-red-300 border-red-400/50",
  contacted:        "bg-blue-500/15 text-blue-300 border-blue-400/40",
  follow_up:        "bg-amber-500/15 text-amber-300 border-amber-400/40",
  quoted:           "bg-cyan-500/15 text-cyan-300 border-cyan-400/40",
  won:              "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  lost:             "bg-white/10 text-white/50 border-white/15",
  not_interested:   "bg-white/10 text-white/45 border-white/15",
  do_not_contact:   "bg-red-500/10 text-red-400 border-red-500/30",
};

const PRIORITY_STYLES = {
  hot:  "bg-red-500/20 text-red-300 border-red-400/50",
  warm: "bg-amber-500/20 text-amber-300 border-amber-400/50",
  low:  "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
};

function priorityTier(score) {
  if (score >= 80) return "hot";
  if (score >= 60) return "warm";
  return "low";
}

// Poll a lead background job every ~1.5s until it settles.
// Returns final job doc on "done"; throws on "error"/timeout.
async function pollLeadJob(jobId, pw, onProgress) {
  const started = Date.now();
  const MAX_MS = 20 * 60 * 1000; // 20 min ceiling
  for (;;) {
    if (Date.now() - started > MAX_MS) throw new Error("Job polling timed out");
    let doc;
    try {
      const { data } = await apiClient.get(`/admin/leads/jobs/${jobId}`,
        { headers: { "X-Admin-Password": pw } });
      doc = data;
    } catch (e) {
      if (e?.response?.status === 404) throw new Error("Job not found or expired");
      await new Promise((r) => setTimeout(r, 3000));
      continue;
    }
    if (onProgress) onProgress(doc);
    if (doc.status === "done") return doc;
    if (doc.status === "error") throw new Error(doc.error || "Job failed");
    await new Promise((r) => setTimeout(r, 1500));
  }
}

const TCT_CITIES = [
  "Mississauga", "Toronto", "Brampton", "Etobicoke", "Oakville", "Milton",
  "Burlington", "Vaughan", "Markham", "Richmond Hill", "Scarborough",
  "Ajax", "Pickering", "Whitby", "Oshawa", "Hamilton",
];

export default function LeadsTab({ pw }) {
  const [tab, setTab] = useState("search");
  const headers = useMemo(() => ({ "X-Admin-Password": pw }), [pw]);

  return (
    <div className="space-y-6" data-testid="leads-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-display text-white">Lead Finder</h2>
          <p className="text-xs text-zinc-500 mt-1 font-mono">
            Prospect GTA sports teams, schools, nonprofits, trades & event
            organisers. Manual outreach approval only — nothing sends automatically.
          </p>
        </div>
        <div className="flex gap-2">
          {[
            ["search", "Search", Search],
            ["saved", "Saved Pipeline", Users],
            ["follow-ups", "Follow-ups", Calendar],
          ].map(([k, l, Icon]) => (
            <button key={k} data-testid={`leads-tab-${k}`}
              onClick={() => setTab(k)}
              className={`px-3 py-1.5 border text-xs font-mono flex items-center gap-1.5 ${
                tab === k ? "border-[var(--tc-red)] text-white bg-[var(--tc-red)]/10"
                          : "border-[#222] text-zinc-400 hover:text-white"}`}>
              <Icon size={12} /> {l}
            </button>
          ))}
        </div>
      </div>

      {tab === "search" && <SearchPanel headers={headers} pw={pw} />}
      {tab === "saved" && <SavedPanel headers={headers} pw={pw} />}
      {tab === "follow-ups" && <FollowUpsPanel headers={headers} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Search Panel
// ─────────────────────────────────────────────────────────────────────
function SearchPanel({ headers, pw }) {
  const [keyword, setKeyword] = useState("");
  const [city, setCity] = useState("Mississauga");
  const [radius, setRadius] = useState(15000);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [checked, setChecked] = useState(() => new Set());
  const [presets, setPresets] = useState([]);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [historyStats, setHistoryStats] = useState(null);
  const [skippedHistoryCount, setSkippedHistoryCount] = useState(0);

  useEffect(() => {
    apiClient.get("/admin/leads/categories", { headers })
      .then((r) => setCategories(r.data.items || [])).catch(() => {});
  }, [headers]);

  const loadHistoryStats = useCallback(() => {
    apiClient.get("/admin/leads/scrape-history/stats", { headers })
      .then((r) => setHistoryStats(r.data)).catch(() => {});
  }, [headers]);
  useEffect(() => { loadHistoryStats(); }, [loadHistoryStats]);

  const loadPresets = useCallback(() => {
    apiClient.get("/admin/leads/presets", { headers })
      .then((r) => setPresets(r.data.items || [])).catch(() => {});
  }, [headers]);
  useEffect(() => { loadPresets(); }, [loadPresets]);

  const runSearch = async (kw = keyword, ct = city, rd = radius) => {
    if (!kw.trim()) { toast.error("Enter a keyword or pick a category"); return; }
    setBusy(true);
    setChecked(new Set());
    setSkippedHistoryCount(0);
    try {
      const { data } = await apiClient.post("/admin/leads/search",
        { keyword: kw.trim(), city: ct, radius_m: rd, limit: 20 },
        { headers });
      setResults(data.items || []);
      setSkippedHistoryCount(data.skipped_previously_seen || 0);
      const skipMsg = data.skipped_previously_seen
        ? ` · ${data.skipped_previously_seen} previously-dismissed skipped`
        : "";
      toast.success(`${data.count} new lead(s) found for "${data.query}"${skipMsg}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setBusy(false); }
  };

  const forgetAllHistory = async () => {
    if (!window.confirm(
      `WIPE the entire scrape history (${historyStats?.total_ever_seen || 0} record(s))?\n\n` +
      `After this, every business you've previously seen will show up in searches again — ` +
      `including ones you deliberately deleted.\n\n` +
      `This does NOT touch your currently-saved leads. It just clears the "do not re-suggest" list.`
    )) return;
    try {
      const { data } = await apiClient.post(
        "/admin/leads/scrape-history/forget",
        { all: true }, { headers });
      toast.success(`Scrape history wiped — ${data.deleted} record(s) removed`);
      loadHistoryStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forget failed");
    }
  };

  const search = () => runSearch();

  const applyPreset = async (p) => {
    setKeyword(p.keyword || "");
    setCity(p.city || "Mississauga");
    setRadius(p.radius_m || 15000);
    await runSearch(p.keyword, p.city, p.radius_m);
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error("Give the preset a name"); return; }
    if (!keyword.trim()) { toast.error("Enter a keyword first"); return; }
    try {
      await apiClient.post("/admin/leads/presets",
        { name, keyword: keyword.trim(), city, radius_m: radius },
        { headers });
      toast.success(`Preset "${name}" saved`);
      setShowSaveForm(false);
      setPresetName("");
      loadPresets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save preset failed");
    }
  };

  const deletePreset = async (p) => {
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    try {
      await apiClient.delete(`/admin/leads/presets/${p.id}`, { headers });
      setPresets(presets.filter((x) => x.id !== p.id));
      toast.success("Preset deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  const toggle = (id) => setChecked((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => {
    const eligible = results.filter((r) => !r.already_saved).map((r) => r.google_place_id);
    setChecked((s) => eligible.every((id) => s.has(id)) ? new Set()
                                                          : new Set(eligible));
  };

  const saveSelected = async () => {
    const picks = results.filter((r) => checked.has(r.google_place_id));
    if (picks.length === 0) { toast.error("Nothing selected"); return; }
    setBusy(true);
    try {
      const { data } = await apiClient.post("/admin/leads/save",
        { leads: picks, service_focus: keyword.trim() || null, on_duplicate: "skip" },
        { headers });
      toast.success(
        `Saved ${data.saved_count} · Updated ${data.updated_count} · Skipped ${data.skipped_count} dup(s)`,
        { duration: 8000 });
      setResults(results.map((r) => picks.includes(r) ? { ...r, already_saved: true } : r));
      setChecked(new Set());
      loadHistoryStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="surface-card p-5 space-y-4" data-testid="leads-search-panel">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_180px_auto] gap-3">
          <input data-testid="leads-search-keyword" value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="e.g. youth soccer clubs, elementary schools, banquet halls…"
            className="input-tc" />
          <select data-testid="leads-search-city" value={city}
            onChange={(e) => setCity(e.target.value)} className="input-tc">
            {TCT_CITIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select value={radius} onChange={(e) => setRadius(Number(e.target.value))}
            className="input-tc">
            <option value={5000}>5 km</option>
            <option value={10000}>10 km</option>
            <option value={15000}>15 km</option>
            <option value={25000}>25 km</option>
            <option value={50000}>50 km</option>
          </select>
          <button data-testid="leads-search-btn" onClick={search} disabled={busy}
            className="btn-tc-primary text-xs flex items-center gap-2 disabled:opacity-40">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Search
          </button>
        </div>

        {/* Saved search presets */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-mono tracking-widest text-zinc-500 flex items-center gap-1.5">
              <Bookmark size={11} /> SAVED SEARCHES
            </div>
            <button data-testid="leads-preset-save-toggle"
              onClick={() => setShowSaveForm((s) => !s)}
              className="text-[10px] font-mono border border-[#222] px-2 py-1 hover:border-[var(--tc-red)] hover:text-white text-zinc-400 flex items-center gap-1">
              <Plus size={10} /> Save current
            </button>
          </div>
          {showSaveForm && (
            <div className="flex gap-2 mb-2" data-testid="leads-preset-save-form">
              <input value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && savePreset()}
                placeholder="Preset name (e.g. GTA sports leagues)"
                className="input-tc text-xs flex-1"
                data-testid="leads-preset-name-input" />
              <button onClick={savePreset} data-testid="leads-preset-save-btn"
                className="btn-tc-primary text-xs px-3">Save</button>
              <button onClick={() => { setShowSaveForm(false); setPresetName(""); }}
                className="text-xs text-zinc-400 hover:text-white px-2">Cancel</button>
            </div>
          )}
          {presets.length === 0 ? (
            <div className="text-[10px] text-zinc-600 font-mono">
              No presets yet — search then click &ldquo;Save current&rdquo; to store it.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="leads-preset-list">
              {presets.map((p) => (
                <div key={p.id} className="flex items-center border border-[#222] hover:border-[var(--tc-red)]"
                  data-testid={`leads-preset-${p.id}`}>
                  <button onClick={() => applyPreset(p)}
                    className="text-[10px] font-mono px-2 py-1 text-zinc-300 hover:text-white flex items-center gap-1"
                    title={`${p.keyword} · ${p.city || "any"} · ${(p.radius_m/1000)|0}km`}>
                    <Bookmark size={10} /> {p.name}
                  </button>
                  <button onClick={() => deletePreset(p)}
                    data-testid={`leads-preset-delete-${p.id}`}
                    className="border-l border-[#222] px-1.5 py-1 text-zinc-500 hover:text-red-400">
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-[10px] font-mono tracking-widest text-zinc-500 mb-2">QUICK PICKS</div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button key={c.keyword} onClick={() => setKeyword(c.keyword)}
                className={`text-[10px] font-mono border px-2 py-1 hover:border-[var(--tc-red)] ${
                  keyword === c.keyword ? "border-[var(--tc-red)] text-white" : "border-[#222] text-zinc-400"}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Scrape-history: dedup shield */}
        {historyStats && historyStats.total_ever_seen > 0 && (
          <div className="flex items-center justify-between border-t border-[#111] pt-3 text-[10px] font-mono"
            data-testid="leads-scrape-history-stats">
            <div className="text-zinc-500 flex flex-wrap items-center gap-3">
              <span className="text-zinc-400"><ShieldCheck size={11} className="inline mr-1" />
                DEDUP SHIELD ACTIVE
              </span>
              <span>Ever scraped: <span className="text-zinc-200">{historyStats.total_ever_seen}</span></span>
              <span>Currently saved: <span className="text-zinc-200">{historyStats.currently_saved}</span></span>
              {historyStats.deleted_but_remembered > 0 && (
                <span>Deleted-but-remembered:
                  <span className="text-amber-300 ml-1">{historyStats.deleted_but_remembered}</span>
                </span>
              )}
              {skippedHistoryCount > 0 && (
                <span className="text-amber-300">
                  · Last search skipped {skippedHistoryCount} previously-dismissed
                </span>
              )}
            </div>
            <button onClick={forgetAllHistory}
              data-testid="leads-forget-history-btn"
              className="text-[10px] font-mono border border-red-500/40 text-red-300 hover:bg-red-900/20 px-2 py-1"
              title="Wipe the dedup list — every previously-seen business will show up again next search">
              Forget history
            </button>
          </div>
        )}
      </div>

      {results.length > 0 && (
        <div className="surface-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-mono text-zinc-400">
              {results.length} result(s) · {checked.size} selected · Hot ({results.filter(r => r.priority === "hot").length}) · Warm ({results.filter(r => r.priority === "warm").length}) · Low ({results.filter(r => r.priority === "low").length})
            </div>
            <div className="flex gap-2">
              <button onClick={toggleAll} className="text-[10px] font-mono border border-[#222] px-2 py-1 hover:text-white">
                {results.filter(r => !r.already_saved).every(r => checked.has(r.google_place_id)) ? "Deselect all" : "Select all"}
              </button>
              <button data-testid="leads-save-selected-btn" onClick={saveSelected}
                disabled={busy || checked.size === 0}
                className="btn-tc-primary text-xs flex items-center gap-1.5 disabled:opacity-40">
                <Save size={12} /> Save {checked.size || ""}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {results.map((r) => <LeadCard key={r.google_place_id} lead={r} checked={checked.has(r.google_place_id)} onToggle={() => toggle(r.google_place_id)} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function LeadCard({ lead, checked, onToggle }) {
  const p = lead.priority || priorityTier(lead.lead_score || 0);
  return (
    <div className={`border p-3 flex items-start gap-3 ${
      lead.already_saved ? "border-emerald-500/30 bg-emerald-500/5"
                          : checked ? "border-[var(--tc-red)]" : "border-[#222]"}`}
      data-testid={`leads-card-${lead.google_place_id}`}>
      {!lead.already_saved && (
        <input type="checkbox" checked={checked} onChange={onToggle}
          className="mt-1" data-testid={`leads-check-${lead.google_place_id}`} />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-white font-mono text-sm truncate">{lead.business_name}</div>
          <div className="flex gap-1">
            <span className={`text-[9px] font-mono px-1.5 py-0.5 border ${PRIORITY_STYLES[p]}`}>
              {p.toUpperCase()} · {lead.lead_score}
            </span>
            {lead.already_saved && (
              <span className="text-[9px] font-mono px-1.5 py-0.5 border border-emerald-400/40 text-emerald-300 bg-emerald-500/10">
                SAVED
              </span>
            )}
          </div>
        </div>
        <div className="text-[11px] text-zinc-400 mt-1 flex flex-wrap gap-3">
          {lead.category && <span>{lead.category}</span>}
          {lead.city && <span className="flex items-center gap-1"><MapPin size={10} />{lead.city}</span>}
          {lead.phone && <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-white"><Phone size={10} />{lead.phone}</a>}
          {lead.website && <a href={lead.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-white"><Globe size={10} />site<ExternalLink size={9} /></a>}
          {lead.rating && <span className="flex items-center gap-1"><Star size={10} />{lead.rating} ({lead.review_count})</span>}
        </div>
        <div className="text-[10px] text-emerald-300/70 mt-1 italic">
          Pitch: {lead.suggested_service}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Saved Pipeline Panel
// ─────────────────────────────────────────────────────────────────────
function SavedPanel({ headers, pw }) {
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [active, setActive] = useState(null); // lead detail modal
  const [selected, setSelected] = useState(() => new Set());
  const [enriching, setEnriching] = useState(false);
  const [enrichProgress, setEnrichProgress] = useState(null); // {completed,total,message}
  const [autoEnrich, setAutoEnrich] = useState(null); // {auto_enrich_enabled, auto_enrich_window_hours}
  const [outreachSending, setOutreachSending] = useState(false);
  const [outreachProgress, setOutreachProgress] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [draftProgress, setDraftProgress] = useState(null);
  const [bulkStatusValue, setBulkStatusValue] = useState("");
  const [bulkStatusBusy, setBulkStatusBusy] = useState(false);
  const [previewFor, setPreviewFor] = useState(null); // { html, subject, to, from }
  const [statusCounts, setStatusCounts] = useState({ by_status: {}, by_priority: {}, total: 0 });

  const loadCounts = useCallback(() => {
    apiClient.get("/admin/leads/status-counts", { headers })
      .then((r) => setStatusCounts(r.data)).catch(() => {});
  }, [headers]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      params.set("page_size", "50");
      if (priority) params.set("priority", priority);
      if (status) params.set("status", status);
      if (search) params.set("search", search);
      const { data } = await apiClient.get(`/admin/leads/saved?${params}`, { headers });
      setItems(data.items || []);
      setTotal(data.total || 0);
      // Reset selection whenever the visible set changes — user chose
      // "current tab only" scoping.
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  }, [priority, status, search, headers]);

  useEffect(() => { load(); loadCounts(); }, [load, loadCounts]);
  useEffect(() => {
    apiClient.get("/admin/leads/statuses", { headers })
      .then((r) => setStatuses(r.data.items || [])).catch(() => {});
    apiClient.get("/admin/leads/settings", { headers })
      .then((r) => setAutoEnrich(r.data)).catch(() => {});
  }, [headers]);

  const toggleAutoEnrich = async () => {
    if (!autoEnrich) return;
    const next = !autoEnrich.auto_enrich_enabled;
    setAutoEnrich({ ...autoEnrich, auto_enrich_enabled: next });
    try {
      const { data } = await apiClient.patch("/admin/leads/settings",
        { auto_enrich_enabled: next }, { headers });
      setAutoEnrich(data);
      toast.success(next
        ? "Nightly auto-enrich ON — leads added in the last 24h will be scanned at 03:30 daily"
        : "Nightly auto-enrich OFF");
    } catch (e) {
      setAutoEnrich({ ...autoEnrich, auto_enrich_enabled: !next });
      toast.error("Could not save toggle");
    }
  };

  const toggleAutoOutreachDraft = async () => {
    if (!autoEnrich) return;
    const next = !autoEnrich.auto_outreach_draft_enabled;
    setAutoEnrich({ ...autoEnrich, auto_outreach_draft_enabled: next });
    try {
      const { data } = await apiClient.patch("/admin/leads/settings",
        { auto_outreach_draft_enabled: next }, { headers });
      setAutoEnrich(data);
      toast.success(next
        ? "AI outreach drafts will be generated after each auto-enrich (never sent — parked on the lead for review)"
        : "AI outreach drafts OFF");
    } catch (e) {
      setAutoEnrich({ ...autoEnrich, auto_outreach_draft_enabled: !next });
      toast.error("Could not save toggle");
    }
  };

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const enrichable = items.filter((i) => !i.enriched_at &&
                                              ((i.website || "").trim()));
  const withEmail = items.filter((i) => (i.email || "").trim());
  const allEnrichableSelected = enrichable.length > 0
    && enrichable.every((i) => selected.has(i.id));
  const allWithEmailSelected = withEmail.length > 0
    && withEmail.every((i) => selected.has(i.id));

  const selectAllWithWebsite = () => {
    if (allEnrichableSelected) setSelected(new Set());
    else setSelected(new Set(enrichable.map((i) => i.id)));
  };
  const selectAllWithEmail = () => {
    if (allWithEmailSelected) setSelected(new Set());
    else setSelected(new Set(withEmail.map((i) => i.id)));
  };

  const bulkEnrich = async () => {
    const ids = [...selected];
    if (ids.length === 0) { toast.error("Nothing selected"); return; }
    setEnriching(true);
    setEnrichProgress({ completed: 0, total: ids.length, message: "Starting…" });
    try {
      const { data } = await apiClient.post("/admin/leads/bulk-enrich",
        { ids, skip_enriched: true }, { headers });
      const final = await pollLeadJob(data.job_id, pw, (doc) => {
        setEnrichProgress({
          completed: doc.completed || 0,
          total: doc.total || ids.length,
          message: doc.message || "",
        });
      });
      const c = final.counts || {};
      toast.success(
        `Enrich done · Updated ${c.updated || 0} · Skipped-already ${c.already_enriched || 0} · No-info ${c.nothing_found || 0} · No-site ${c.no_website || 0} · Errors ${c.error || 0}`,
        { duration: 9000 });
      setSelected(new Set());
      load();
      loadCounts();
    } catch (e) {
      toast.error(e?.message || "Bulk enrich failed");
    } finally {
      setEnriching(false);
      setEnrichProgress(null);
    }
  };

  const bulkDraft = async () => {
    const ids = [...selected];
    if (ids.length === 0) { toast.error("Nothing selected"); return; }
    if (!window.confirm(
      `Generate PERSONALISED AI outreach drafts for ${ids.length} lead(s)?\n\n` +
      `Each draft is tailored per-lead (business name, category, city, score) — ` +
      `NOT a generic template. Nothing is sent — drafts land on each lead for review.\n\n` +
      `Leads that already have a draft are skipped by default.`
    )) return;
    setDrafting(true);
    setDraftProgress({ completed: 0, total: ids.length, message: "Starting…" });
    try {
      const { data } = await apiClient.post("/admin/leads/bulk-draft",
        { ids, overwrite_existing: false }, { headers });
      const final = await pollLeadJob(data.job_id, pw, (doc) => {
        setDraftProgress({
          completed: doc.completed || 0,
          total: doc.total || ids.length,
          message: doc.message || "",
        });
      });
      const c = final.counts || {};
      toast.success(
        `AI drafts done · Drafted ${c.drafted || 0} · Already-drafted ${c.already_drafted || 0} · Errors ${c.error || 0}`,
        { duration: 9000 });
      load();
    } catch (e) {
      toast.error(e?.message || "Bulk draft failed");
    } finally {
      setDrafting(false);
      setDraftProgress(null);
    }
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (ids.length === 0) { toast.error("Nothing selected"); return; }
    if (!window.confirm(
      `DELETE ${ids.length} lead(s) permanently?\n\n` +
      `This removes them from the database along with all activity, drafts, and reply history. ` +
      `You can re-add them by re-running the search that found them.`
    )) return;
    try {
      const { data } = await apiClient.post("/admin/leads/bulk-delete",
        { ids }, { headers });
      toast.success(`Deleted ${data.deleted} lead(s)`);
      setSelected(new Set());
      load();
      loadCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
  };

  // Subset of `selected` that actually has an email (for outreach button).
  const emailableSelected = withEmail.filter((i) => selected.has(i.id));

  const bulkOutreach = async () => {
    const ids = emailableSelected.map((i) => i.id);
    if (ids.length === 0) {
      toast.error("None of the selected leads have an email — enrich them first");
      return;
    }
    const alreadySent = emailableSelected.filter((i) => i.last_outreach_sent_at).length;
    let force = false;
    if (alreadySent > 0) {
      if (!window.confirm(
        `${ids.length} lead(s) selected · ${alreadySent} already have outreach sent within 48h.\n\n` +
        `Click OK to SKIP those (respect the 48h cooldown), or Cancel to abort.\n\n` +
        `(To override the cooldown, use the single-lead Send button on each lead.)`
      )) return;
    } else if (!window.confirm(
      `Send PERSONALISED AI-drafted outreach to ${ids.length} lead(s)?\n\n` +
      `Each message is tailored per-lead (business name, category, city, score) — ` +
      `NOT a generic template that goes to everyone.\n\n` +
      `From: torontocustomt@gmail.com (shop SMTP)\n` +
      `Any lead without a draft will get one auto-generated by AI first.\n` +
      `Successfully-sent leads will be moved to "Contacted" status.`
    )) return;

    setOutreachSending(true);
    setOutreachProgress({ completed: 0, total: ids.length, message: "Preparing…" });
    try {
      const { data } = await apiClient.post("/admin/leads/bulk-outreach-send",
        { ids, force, auto_generate_missing: true },
        { headers });
      const final = await pollLeadJob(data.job_id, pw, (doc) => {
        setOutreachProgress({
          completed: doc.completed || 0,
          total: doc.total || ids.length,
          message: doc.message || "",
        });
      });
      const c = final.counts || {};
      toast.success(
        `Outreach batch done · Sent ${c.sent || 0} · Cooldown ${c.cooldown || 0} · ` +
        `No email ${c.no_email || 0} · Errors ${(c.send_error || 0) + (c.draft_error || 0)}`,
        { duration: 10000 });
      setSelected(new Set());
      load();
      loadCounts();
    } catch (e) {
      toast.error(e?.message || "Bulk outreach failed");
    } finally {
      setOutreachSending(false);
      setOutreachProgress(null);
    }
  };

  const bulkStatusChange = async () => {
    if (!bulkStatusValue) return;
    if (selected.size === 0) { toast.error("Nothing selected"); return; }
    const label = statuses.find((s) => s.value === bulkStatusValue)?.label
      || bulkStatusValue;
    if (!window.confirm(
      `Move ${selected.size} lead(s) to "${label}" status?`
    )) return;
    setBulkStatusBusy(true);
    try {
      const { data } = await apiClient.post("/admin/leads/bulk-status",
        { ids: [...selected], status: bulkStatusValue },
        { headers });
      toast.success(`${data.modified} lead(s) moved to ${data.label}`);
      setSelected(new Set());
      setBulkStatusValue("");
      load();
      loadCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk status change failed");
    } finally {
      setBulkStatusBusy(false);
    }
  };

  const toggleReplyTracker = async () => {
    if (!autoEnrich) return;
    const next = !autoEnrich.reply_tracker_enabled;
    setAutoEnrich({ ...autoEnrich, reply_tracker_enabled: next });
    try {
      const { data } = await apiClient.patch("/admin/leads/settings",
        { reply_tracker_enabled: next }, { headers });
      setAutoEnrich(data);
      toast.success(next
        ? "Reply tracker ON — every 15m we scan the shop Gmail for replies. Any lead who replies gets moved to Hot Lead."
        : "Reply tracker OFF");
    } catch (e) {
      setAutoEnrich({ ...autoEnrich, reply_tracker_enabled: !next });
      toast.error("Could not save toggle");
    }
  };

  const pollRepliesNow = async () => {
    toast.info("Polling Gmail for replies…");
    try {
      const { data } = await apiClient.post("/admin/leads/replies/poll",
        {}, { headers });
      if (data.skipped) {
        toast.warning(`Skipped: ${data.reason}`);
      } else {
        toast.success(
          `Polled ${data.polled || 0} reply(ies) · Promoted ${data.promoted || 0} to Hot Lead · Watched ${data.watched_leads} lead(s)`);
        load();
        loadCounts();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Poll failed");
    }
  };

  const previewFirstOutreach = async () => {
    // Take the first selected lead with a draft or a name for preview.
    const target = emailableSelected[0] || items.find((i) => selected.has(i.id));
    if (!target) { toast.error("Select at least one lead to preview"); return; }
    try {
      const { data } = await apiClient.post(
        `/admin/leads/${target.id}/outreach-preview`,
        {}, { headers });
      setPreviewFor({ ...data, lead: target });
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || "Nothing to preview — draft one first for this lead");
    }
  };


  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (priority) params.set("priority", priority);
    if (status) params.set("status", status);
    if (search) params.set("search", search);
    try {
      const r = await apiClient.get(`/admin/leads/export.csv?${params}`,
        { headers, responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `tct-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
    } catch (e) { toast.error("Export failed"); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead? History will be removed.")) return;
    try {
      await apiClient.delete(`/admin/leads/${id}`, { headers });
      toast.success("Deleted");
      setItems(items.filter((i) => i.id !== id));
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="surface-card p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <div className="text-[9px] font-mono text-zinc-500 mb-1">PRIORITY</div>
            <div className="flex gap-1">
              {["", "hot", "warm", "low"].map((p) => (
                <button key={p || "all"} onClick={() => setPriority(p)}
                  data-testid={`leads-priority-${p || "all"}`}
                  className={`text-[10px] font-mono border px-2 py-1 ${
                    priority === p ? "border-[var(--tc-red)] text-white"
                                    : "border-[#222] text-zinc-400"}`}>
                  {p ? `${p.toUpperCase()} (${statusCounts.by_priority?.[p] ?? 0})` : `ALL (${statusCounts.total ?? 0})`}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[9px] font-mono text-zinc-500 mb-1">SEARCH</div>
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, phone, website…" className="input-tc text-xs w-full" />
          </div>
          <button onClick={() => { load(); loadCounts(); }} disabled={busy}
            className="btn-tc-secondary text-xs flex items-center gap-1.5">
            <Filter size={12} /> Refresh
          </button>
          <button onClick={exportCsv}
            className="btn-tc-secondary text-xs flex items-center gap-1.5">
            <Download size={12} /> CSV
          </button>
        </div>
        {/* Status filter chips — one-tap per status with counts */}
        <div>
          <div className="text-[9px] font-mono text-zinc-500 mb-1.5">STATUS</div>
          <div className="flex flex-wrap gap-1.5" data-testid="leads-status-chips">
            <button onClick={() => setStatus("")}
              data-testid="leads-status-chip-all"
              className={`text-[10px] font-mono border px-2 py-1 ${
                status === "" ? "border-[var(--tc-red)] text-white bg-[var(--tc-red)]/10"
                                : "border-[#222] text-zinc-400 hover:text-white"}`}>
              All ({statusCounts.total ?? 0})
            </button>
            {statuses.map((s) => {
              const count = statusCounts.by_status?.[s.value] ?? 0;
              const active = status === s.value;
              return (
                <button key={s.value} onClick={() => setStatus(s.value)}
                  data-testid={`leads-status-chip-${s.value}`}
                  className={`text-[10px] font-mono border px-2 py-1 ${
                    active ? `border-[var(--tc-red)] text-white bg-[var(--tc-red)]/10`
                              : (count === 0 ? "border-[#111] text-zinc-600"
                                              : "border-[#222] text-zinc-300 hover:text-white")
                  }`}>
                  {s.label} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      <div className="border border-[#222] bg-[#0a0a0a] px-3 py-2 space-y-2"
        data-testid="leads-bulk-bar">
        {/* Row 1: selection controls + auto-toggles */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-[11px] font-mono text-zinc-400 flex-wrap">
            <span className="text-zinc-500">
              {selected.size > 0 ? `${selected.size} selected` : "Select:"}
            </span>
            <button onClick={selectAllWithEmail}
              data-testid="leads-select-with-email"
              className={`text-[10px] font-mono border px-2 py-1 hover:text-white ${
                allWithEmailSelected ? "border-[var(--tc-red)] text-white bg-[var(--tc-red)]/10"
                                        : "border-[#222] text-zinc-300"}`}>
              All w/ email ({withEmail.length})
            </button>
            <button onClick={selectAllWithWebsite}
              data-testid="leads-select-with-website"
              className={`text-[10px] font-mono border px-2 py-1 hover:text-white ${
                allEnrichableSelected ? "border-[var(--tc-red)] text-white bg-[var(--tc-red)]/10"
                                        : "border-[#222] text-zinc-300"}`}>
              All needing enrich ({enrichable.length})
            </button>
            <button onClick={() => setSelected(new Set())}
              disabled={selected.size === 0}
              data-testid="leads-select-clear"
              className="text-[10px] font-mono border border-[#222] px-2 py-1 text-zinc-500 hover:text-white disabled:opacity-30">
              Clear
            </button>
          </div>
          <div className="flex items-center gap-3 text-[11px] font-mono text-zinc-400 flex-wrap">
            {autoEnrich && (
              <>
                <label className="flex items-center gap-1.5 cursor-pointer"
                  title="Runs 03:30 daily · enriches leads added in the last 24h">
                  <input type="checkbox"
                    checked={!!autoEnrich.auto_enrich_enabled}
                    onChange={toggleAutoEnrich}
                    data-testid="leads-auto-enrich-toggle" />
                  <span className={autoEnrich.auto_enrich_enabled
                    ? "text-emerald-300" : "text-zinc-400"}>
                    Nightly auto-enrich
                  </span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer"
                  title="After auto-enrich finds an email, auto-generate a cold-outreach draft (never sent)">
                  <input type="checkbox"
                    checked={!!autoEnrich.auto_outreach_draft_enabled}
                    disabled={!autoEnrich.auto_enrich_enabled}
                    onChange={toggleAutoOutreachDraft}
                    data-testid="leads-auto-outreach-toggle" />
                  <span className={autoEnrich.auto_outreach_draft_enabled
                    ? "text-purple-300"
                    : (autoEnrich.auto_enrich_enabled ? "text-zinc-400" : "text-zinc-600")}>
                    + AI outreach draft
                  </span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer"
                  title="Every 15m poll the shop's Gmail inbox for replies. Anyone who replies auto-flips to Hot Lead.">
                  <input type="checkbox"
                    checked={!!autoEnrich.reply_tracker_enabled}
                    onChange={toggleReplyTracker}
                    data-testid="leads-reply-tracker-toggle" />
                  <span className={autoEnrich.reply_tracker_enabled
                    ? "text-orange-300" : "text-zinc-400"}>
                    Reply tracker
                  </span>
                </label>
                {autoEnrich.reply_tracker_enabled && (
                  <button onClick={pollRepliesNow}
                    data-testid="leads-poll-replies-btn"
                    className="text-[10px] font-mono border border-[#222] px-2 py-0.5 hover:border-orange-400 hover:text-white text-zinc-400 flex items-center gap-1"
                    title="Poll Gmail for replies right now">
                    <RefreshCw size={10} /> Poll now
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        {/* Row 2: bulk actions */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {enrichProgress && (
              <span className="text-[10px] font-mono text-emerald-300"
                data-testid="leads-bulk-enrich-progress">
                {enrichProgress.completed}/{enrichProgress.total} · {enrichProgress.message || ""}
              </span>
            )}
            {draftProgress && (
              <span className="text-[10px] font-mono text-purple-300"
                data-testid="leads-bulk-draft-progress">
                {draftProgress.completed}/{draftProgress.total} · {draftProgress.message || ""}
              </span>
            )}
            {outreachProgress && (
              <span className="text-[10px] font-mono text-red-300"
                data-testid="leads-bulk-outreach-progress">
                {outreachProgress.completed}/{outreachProgress.total} · {outreachProgress.message || ""}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={bulkStatusValue}
              onChange={(e) => setBulkStatusValue(e.target.value)}
              data-testid="leads-bulk-status-select"
              disabled={selected.size === 0 || bulkStatusBusy}
              className="input-tc text-[10px] py-1 px-2 disabled:opacity-40"
              title="Move selected leads to a new status">
              <option value="">Move to…</option>
              {statuses.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <button data-testid="leads-bulk-status-apply-btn"
              onClick={bulkStatusChange}
              disabled={!bulkStatusValue || selected.size === 0 || bulkStatusBusy}
              className="btn-tc-secondary text-xs flex items-center gap-1.5 disabled:opacity-40"
              title="Apply the status change to the selected leads">
              {bulkStatusBusy ? <Loader2 size={12} className="animate-spin" />
                                : <CheckCircle2 size={12} />}
              Apply
            </button>
            <button data-testid="leads-bulk-enrich-btn" onClick={bulkEnrich}
              disabled={enriching || drafting || outreachSending || selected.size === 0}
              className="btn-tc-secondary text-xs flex items-center gap-1.5 disabled:opacity-40"
              title="Scrape websites for contact info (already-enriched leads are skipped automatically)">
              {enriching ? <Loader2 size={12} className="animate-spin" />
                           : <Sparkles size={12} />}
              Enrich {selected.size || ""}
            </button>
            <button data-testid="leads-bulk-draft-btn" onClick={bulkDraft}
              disabled={drafting || enriching || outreachSending || selected.size === 0}
              className="btn-tc-secondary text-xs flex items-center gap-1.5 disabled:opacity-40 border-purple-500/40 text-purple-200 hover:bg-purple-900/20"
              title="Generate a personalised AI outreach draft per selected lead (never sends). Leads with an existing draft are skipped.">
              {drafting ? <Loader2 size={12} className="animate-spin" />
                          : <Wand2 size={12} />}
              AI Draft {selected.size || ""}
            </button>
            <button data-testid="leads-bulk-preview-btn"
              onClick={previewFirstOutreach}
              disabled={selected.size === 0}
              className="btn-tc-secondary text-xs flex items-center gap-1.5 disabled:opacity-40"
              title="Preview the rendered email for the first selected lead">
              <Eye size={12} /> Preview
            </button>
            <button data-testid="leads-bulk-outreach-btn" onClick={bulkOutreach}
              disabled={outreachSending || enriching || drafting || emailableSelected.length === 0}
              title={emailableSelected.length === 0
                ? "Select leads that have an email address"
                : `Send PERSONALISED AI-drafted outreach to ${emailableSelected.length} lead(s) · each message is tailored per-lead · auto-marks Contacted`}
              className="btn-tc-primary text-xs flex items-center gap-1.5 disabled:opacity-40">
              {outreachSending ? <Loader2 size={12} className="animate-spin" />
                                : <Send size={12} />}
              Send Outreach {emailableSelected.length || ""}
            </button>
            <button data-testid="leads-bulk-delete-btn" onClick={bulkDelete}
              disabled={selected.size === 0}
              className="text-xs flex items-center gap-1.5 disabled:opacity-40 px-3 py-1.5 border border-red-500/40 text-red-300 hover:bg-red-900/20"
              title="Permanently delete the selected leads">
              <Trash2 size={12} /> Delete {selected.size || ""}
            </button>
          </div>
        </div>
      </div>

      <div className="text-[10px] font-mono text-zinc-500">
        {total} saved lead(s) · showing {items.length}
      </div>

      <div className="space-y-2">
        {items.map((l) => (
          <SavedLeadRow key={l.id} lead={l} statuses={statuses} headers={headers}
            selected={selected.has(l.id)}
            onToggleSelect={() => toggle(l.id)}
            onOpen={() => setActive(l)} onDelete={() => deleteLead(l.id)}
            onUpdated={(u) => setItems(items.map((i) => i.id === u.id ? { ...i, ...u } : i))} />
        ))}
        {items.length === 0 && !busy && (
          <div className="text-zinc-500 text-sm font-mono text-center py-12">
            No saved leads yet — go run a search.
          </div>
        )}
      </div>

      {active && <LeadDetailModal lead={active} headers={headers}
        onClose={() => setActive(null)}
        onRefresh={load} />}

      {previewFor && <OutreachPreviewModal preview={previewFor}
        onClose={() => setPreviewFor(null)} />}
    </div>
  );
}

function SavedLeadRow({ lead, statuses, headers, selected, onToggleSelect, onOpen, onDelete, onUpdated }) {
  const p = lead.priority || priorityTier(lead.lead_score || 0);
  const patch = async (payload) => {
    try {
      await apiClient.patch(`/admin/leads/${lead.id}`, payload, { headers });
      onUpdated({ id: lead.id, ...payload });
    } catch (e) { toast.error("Update failed"); }
  };
  return (
    <div className={`border p-3 flex items-start gap-3 hover:border-zinc-600 ${
        selected ? "border-[var(--tc-red)] bg-[var(--tc-red)]/5" : "border-[#222]"}`}
      data-testid={`leads-row-${lead.id}`}>
      <input type="checkbox" checked={!!selected} onChange={onToggleSelect}
        className="mt-1"
        data-testid={`leads-row-check-${lead.id}`} />
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onOpen}>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-white font-mono text-sm truncate">{lead.business_name}</div>
          <span className={`text-[9px] font-mono px-1.5 py-0.5 border ${PRIORITY_STYLES[p]}`}>{p.toUpperCase()} · {lead.lead_score}</span>
          <span className={`text-[9px] font-mono px-1.5 py-0.5 border ${STATUS_STYLES[lead.status] || STATUS_STYLES.new}`}>
            {statuses.find((s) => s.value === lead.status)?.label || lead.status}
          </span>
          {lead.enriched_at && (
            <span className="text-[9px] font-mono px-1.5 py-0.5 border border-emerald-500/30 text-emerald-300">
              ENRICHED
            </span>
          )}
          {lead.last_reply_at && (
            <span className="text-[9px] font-mono px-1.5 py-0.5 border border-orange-500/40 text-orange-300 flex items-center gap-1"
              title={`Replied ${new Date(lead.last_reply_at).toLocaleString()}${lead.reply_count > 1 ? ` (${lead.reply_count}×)` : ""}\n${lead.last_reply_subject || ""}`}>
              <Inbox size={9} /> REPLIED{lead.reply_count > 1 ? ` ${lead.reply_count}×` : ""}
            </span>
          )}
        </div>
        <div className="text-[11px] text-zinc-400 mt-1 flex flex-wrap gap-3">
          {lead.category && <span>{lead.category}</span>}
          {lead.city && <span>{lead.city}</span>}
          {lead.phone && <span>{lead.phone}</span>}
          {lead.email && (
            <span className="text-emerald-300 flex items-center gap-1">
              {lead.email}
              {lead.email_source === "web_fallback" && (
                <span className="text-[8px] px-1 border border-purple-400/40 text-purple-300"
                  title="Found via web search fallback — verify before contacting">
                  WEB
                </span>
              )}
            </span>
          )}
        </div>
      </div>
      <select value={lead.status} onChange={(e) => patch({ status: e.target.value })}
        className="input-tc text-[10px] py-1 px-1">
        {statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
      <button onClick={onDelete} className="text-zinc-500 hover:text-red-400 p-1">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Lead Detail Modal — enrich, generate outreach, edit notes / follow-up

// ─────────────────────────────────────────────────────────────────────
// Outreach Preview Modal — renders exactly what the recipient sees
// ─────────────────────────────────────────────────────────────────────
function OutreachPreviewModal({ preview, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="leads-outreach-preview-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border border-purple-500/40 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-[#1a1a1a] p-4 flex items-center justify-between sticky top-0 bg-[#0a0a0a]">
          <div>
            <div className="text-[10px] font-mono text-purple-300 tracking-widest">OUTREACH PREVIEW</div>
            <div className="text-white text-sm mt-1">
              {preview.lead?.business_name || "Selected lead"}
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1"
            data-testid="leads-outreach-preview-close-btn">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="border border-[#1a1a1a] bg-[#050505] p-3 text-[11px] font-mono space-y-1">
            <div><span className="text-zinc-500">From:</span> <span className="text-zinc-200">{preview.from || "(sender not configured)"}</span></div>
            <div><span className="text-zinc-500">To:</span> <span className="text-zinc-200">{preview.to || "(no email — enrich first)"}</span></div>
            <div><span className="text-zinc-500">Subject:</span> <span className="text-white">{preview.subject}</span></div>
          </div>
          <div className="text-[9px] font-mono text-zinc-500 tracking-widest">
            RENDERED HTML (what the recipient actually sees):
          </div>
          <div className="border border-purple-500/30 bg-white overflow-x-auto"
            data-testid="leads-outreach-preview-body">
            <div className="p-4"
              dangerouslySetInnerHTML={{ __html: preview.html }} />
          </div>
          <div className="text-[10px] font-mono text-amber-300 border border-amber-500/30 bg-amber-500/5 p-2">
            This is a preview only — nothing has been sent. Close this and hit
            &ldquo;Send Outreach N&rdquo; (or the per-lead Send button) to actually deliver.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function LeadDetailModal({ lead: initial, headers, onClose, onRefresh }) {
  const [lead, setLead] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [notes, setNotes] = useState(initial.notes || "");
  const [followUp, setFollowUp] = useState(initial.follow_up_date || "");
  const [draft, setDraft] = useState(initial.last_outreach_message || "");
  // Keep the local editable draft in sync when a new AI message is generated.
  useEffect(() => { setDraft(lead.last_outreach_message || ""); },
                     [lead.last_outreach_message]);

  const enrich = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/admin/leads/${lead.id}/enrich`,
        {}, { headers });
      const { data: fresh } = await apiClient.get(`/admin/leads/${lead.id}`, { headers });
      setLead(fresh.lead);
      const found = Object.keys(data.applied || {}).length;
      toast.success(found ? `Enriched — added ${Object.keys(data.applied).join(", ")}`
                             : "Scanned website · no new contact info");
    } catch (e) { toast.error(e?.response?.data?.detail || "Enrich failed"); }
    finally { setBusy(false); }
  };

  const generateOutreach = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/admin/leads/${lead.id}/outreach-message`,
        {}, { headers });
      setLead({ ...lead, last_outreach_message: data.message });
      toast.success("Outreach draft generated — edit as needed, then Send");
    } catch (e) { toast.error(e?.response?.data?.detail || "Outreach failed"); }
    finally { setBusy(false); }
  };

  // Cooldown calc — button greys out until 48h since last send.
  const cooldownInfo = (() => {
    if (!lead.last_outreach_sent_at) return { blocked: false, hoursLeft: 0 };
    const then = new Date(lead.last_outreach_sent_at).getTime();
    const hoursSince = (Date.now() - then) / 3600000;
    if (hoursSince >= 48) return { blocked: false, hoursLeft: 0 };
    return { blocked: true, hoursLeft: 48 - hoursSince };
  })();

  const sendOutreach = async (force = false) => {
    if (!lead.email) { toast.error("Lead has no email — enrich the lead first"); return; }
    if (!draft.trim()) { toast.error("Draft is empty — generate or type a message"); return; }
    if (cooldownInfo.blocked && !force) {
      const hrs = cooldownInfo.hoursLeft.toFixed(1);
      if (!window.confirm(
        `Last outreach to ${lead.email} was sent ${(48 - cooldownInfo.hoursLeft).toFixed(1)}h ago. ` +
        `Cooldown wants ${hrs}h more before the next send.\n\n` +
        `Override cooldown and send anyway?`
      )) return;
      force = true;
    } else if (!window.confirm(
      `Send outreach email to ${lead.email}?\n\n` +
      `From: torontocustomt@gmail.com (shop SMTP)\n` +
      `Subject: Toronto Custom T-Shirts — quick idea for ${lead.business_name}\n\n` +
      `The message you see in the box is what will be sent.`
    )) return;

    setSending(true);
    try {
      const { data } = await apiClient.post(
        `/admin/leads/${lead.id}/outreach-send`,
        { message: draft, force },
        { headers });
      toast.success(`Sent to ${data.sent_to}`);
      setLead({
        ...lead,
        last_outreach_message: draft,
        last_outreach_sent_at: data.sent_at,
        last_outreach_sent_to: data.sent_to,
        outreach_sent_count: (lead.outreach_sent_count || 0) + 1,
      });
      onRefresh?.();
    } catch (e) {
      const status = e?.response?.status;
      toast.error(e?.response?.data?.detail
        || (status === 429 ? "Cooldown active — try again later" : "Send failed"));
    } finally {
      setSending(false);
    }
  };

  const save = async () => {
    try {
      await apiClient.patch(`/admin/leads/${lead.id}`,
        { notes, follow_up_date: followUp || "" }, { headers });
      toast.success("Saved");
      onRefresh?.();
      onClose();
    } catch (e) { toast.error("Save failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose} data-testid="leads-detail-modal">
      <div className="bg-black border border-[#222] max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-white font-display text-xl">{lead.business_name}</div>
            <div className="text-[11px] text-zinc-400 mt-1">
              {lead.category} · {lead.city}
              {lead.rating && ` · ${lead.rating}★ (${lead.review_count})`}
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white text-sm">✕</button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs mb-4">
          {lead.phone && <a href={`tel:${lead.phone}`} className="border border-[#222] p-2 flex items-center gap-2"><Phone size={12} />{lead.phone}</a>}
          {lead.email && <a href={`mailto:${lead.email}`} className="border border-[#222] p-2 flex items-center gap-2 text-emerald-300"><Mail size={12} />{lead.email}</a>}
          {lead.website && <a href={lead.website} target="_blank" rel="noreferrer" className="border border-[#222] p-2 flex items-center gap-2"><Globe size={12} />Website<ExternalLink size={10} /></a>}
          {lead.google_maps_url && <a href={lead.google_maps_url} target="_blank" rel="noreferrer" className="border border-[#222] p-2 flex items-center gap-2"><MapPin size={12} />Google Maps<ExternalLink size={10} /></a>}
        </div>

        <div className="border border-emerald-500/30 bg-emerald-500/5 p-3 mb-4">
          <div className="text-[10px] font-mono text-emerald-400 mb-1">TCT PITCH</div>
          <div className="text-emerald-100 text-sm">{lead.suggested_service}</div>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          <button data-testid="leads-enrich-btn" onClick={enrich} disabled={busy || !lead.website}
            className="btn-tc-secondary text-xs flex items-center gap-1.5 disabled:opacity-40">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            Enrich from website
          </button>
          <button data-testid="leads-outreach-btn" onClick={generateOutreach} disabled={busy}
            className="btn-tc-primary text-xs flex items-center gap-1.5 disabled:opacity-40">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
            Draft outreach message
          </button>
        </div>

        {(lead.last_outreach_message || draft) && (
          <div className="border border-purple-500/40 bg-purple-500/5 p-3 mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono text-purple-300">
                OUTREACH DRAFT — edit as needed, then send
                {lead.last_outreach_sent_at && (
                  <span className="ml-2 text-emerald-300">
                    · last sent {new Date(lead.last_outreach_sent_at).toLocaleString()}
                    {lead.outreach_sent_count > 1 && ` (${lead.outreach_sent_count}×)`}
                  </span>
                )}
              </div>
              <button onClick={() => { navigator.clipboard.writeText(draft); toast.success("Copied"); }}
                className="text-purple-300 hover:text-white"
                data-testid="leads-outreach-copy-btn"
                title="Copy to clipboard">
                <Copy size={12} />
              </button>
            </div>
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
              rows={8} data-testid="leads-outreach-draft"
              className="input-tc w-full text-sm text-white bg-black/40 font-sans leading-relaxed"
              placeholder="Draft will appear here after clicking Draft outreach message…" />
            <div className="mt-2 text-[10px] font-mono text-zinc-500">
              Subject: <span className="text-zinc-300">Toronto Custom T-Shirts — quick idea for {lead.business_name}</span>
              <span className="mx-2">·</span>
              To: <span className="text-zinc-300">{lead.email || "(no email — enrich first)"}</span>
              <span className="mx-2">·</span>
              From: <span className="text-zinc-300">torontocustomt@gmail.com</span>
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button onClick={() => sendOutreach(false)}
                disabled={sending || !lead.email || !draft.trim()}
                data-testid="leads-outreach-send-btn"
                className="btn-tc-primary text-xs flex items-center gap-1.5 disabled:opacity-40"
                title={cooldownInfo.blocked
                  ? `Cooldown: ${cooldownInfo.hoursLeft.toFixed(1)}h left (48h since last send). Click to override.`
                  : "Send this outreach email via the shop's Gmail SMTP"}>
                {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                {sending ? "Sending…"
                  : cooldownInfo.blocked
                    ? `Send (override · ${cooldownInfo.hoursLeft.toFixed(0)}h cooldown)`
                    : "Send Email"}
              </button>
              {cooldownInfo.blocked && (
                <span className="text-[10px] font-mono text-amber-300"
                  data-testid="leads-outreach-cooldown">
                  48h cooldown active — override at your own risk
                </span>
              )}
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <div className="text-[10px] font-mono text-zinc-500 mb-1">NOTES</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={3} className="input-tc w-full text-xs"
              placeholder="Call outcome, quote details, decision-maker name…" />
          </div>
          <div>
            <div className="text-[10px] font-mono text-zinc-500 mb-1">FOLLOW-UP DATE</div>
            <input type="date" value={followUp || ""}
              onChange={(e) => setFollowUp(e.target.value)}
              className="input-tc text-xs" />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs text-zinc-400 hover:text-white px-3 py-1.5">Cancel</button>
          <button onClick={save} className="btn-tc-primary text-xs flex items-center gap-1.5">
            <CheckCircle2 size={12} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Follow-ups dashboard
// ─────────────────────────────────────────────────────────────────────
function FollowUpSection({ title, items, tone }) {
  return (
    <div className="surface-card p-4">
      <div className={`text-xs font-mono mb-3 ${tone}`}>{title} · {items.length}</div>
      <div className="space-y-1">
        {items.length === 0 && <div className="text-zinc-600 text-xs">Nothing here</div>}
        {items.map((l) => (
          <div key={l.id} className="text-xs text-zinc-300 flex justify-between border-b border-[#111] py-1">
            <span>{l.business_name}</span>
            <span className="text-zinc-500">{l.follow_up_date}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FollowUpsPanel({ headers }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    apiClient.get("/admin/leads/follow-ups", { headers })
      .then((r) => setData(r.data)).catch(() => {});
  }, [headers]);
  if (!data) return <div className="text-zinc-500 font-mono text-sm">Loading follow-ups…</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <FollowUpSection title="OVERDUE" items={data.overdue} tone="text-red-400" />
      <FollowUpSection title="TODAY" items={data.today} tone="text-amber-400" />
      <FollowUpSection title="THIS WEEK" items={data.this_week} tone="text-cyan-400" />
    </div>
  );
}
